import React, { useEffect, useMemo, useRef, useState } from 'react';
import AiTakeoverControls from './AiTakeoverControls';
import AssignControl from './AssignControl';
import ContextRail from './ContextRail';
import {
  consentView, nextAppointment, formatApptWhen, timeOf, dayLabel, humanize,
  escalationReason, formatPhone, initialsOf, shortDate, buildThreadSegments,
} from './threadUtils';
import '../../styles/MessagingThread.css';

const RAIL_PIN_KEY = 'nh.messaging.rail';

/**
 * The conversation as a ledger: amber receipts for the automated reminders the
 * patient also received, colour-railed bubbles for patient / staff / AI, event
 * pills for escalation, takeover and resolution, and a context rail that opens
 * itself when something needs attention and collapses to icons when nothing does.
 *
 * The composer is passed as `children` so the rail can run alongside it.
 */
function ConversationThread({
  conversation, messages, loading, onResolve, consentStatus, onGrantConsent, onRevokeConsent,
  practiceId, onAiStatusChange, appointments, consents, insights, documents = [], documentCategories = [], busyDocumentId = null,
  patient = null, summary = null, summaryLoading = false, onRefreshSummary = null,
  onBookingLink, onRequestDocument, onSendDocuments, onOpenDocument, onRecategorize, onPushDocument, children,
}) {
  const endRef = useRef(null);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolveNotes, setResolveNotes] = useState('');
  // Resolved threads fold to one row; this is the set a user has opened back up.
  const [expandedThreads, setExpandedThreads] = useState(() => new Set());
  const [showThreadMenu, setShowThreadMenu] = useState(false);
  const segRefs = useRef({});
  const [openReceipts, setOpenReceipts] = useState(() => new Set());
  const [pin, setPinState] = useState(() => {
    try { return localStorage.getItem(RAIL_PIN_KEY) || 'auto'; } catch { return 'auto'; }
  });
  // A manual expand/collapse that is not pinned lasts for the current conversation only.
  const [override, setOverride] = useState(null);

  // The thread re-polls every few seconds. Only follow new messages when the reader is
  // already at the bottom; otherwise scrolling back through history gets yanked to the
  // latest message on every poll (commons #396). Jumping to the end on a conversation
  // switch is still right, so that case ignores the position check.
  const msgsRef = useRef(null);
  const atBottom = () => {
    const el = msgsRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };
  const stickRef = useRef(true);
  useEffect(() => {
    if (!stickRef.current) return;
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  useEffect(() => {
    stickRef.current = true;
    endRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [conversation?.id]);
  useEffect(() => {
    setOverride(null); setOpenReceipts(new Set()); setExpandedThreads(new Set()); setShowThreadMenu(false);
  }, [conversation?.id]);
  useEffect(() => {
    if (!showThreadMenu) return undefined;
    const close = (e) => { if (!e.target.closest?.('.nh-threads')) setShowThreadMenu(false); };
    const esc = (e) => { if (e.key === 'Escape') setShowThreadMenu(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [showThreadMenu]);

  const setPin = (v) => {
    setPinState(v);
    setOverride(null);
    try { localStorage.setItem(RAIL_PIN_KEY, v); } catch { /* storage unavailable */ }
  };

  // ---- derived context -------------------------------------------------
  const activeSub = conversation?.active_sub_conversation || null;
  const escalation = useMemo(() => {
    if (!activeSub) return null;
    const open = activeSub.ai_takeover_status === 'escalated' || Number(activeSub.needs_human_attention) === 1;
    if (!open) return null;
    return {
      sub: activeSub,
      reasonLabel: humanize(escalationReason(activeSub.escalation_reason)),
      raised: activeSub.escalated_at ? `${shortDate(activeSub.escalated_at)} ${timeOf(activeSub.escalated_at)} by AI` : null,
    };
  }, [activeSub]);
  const appointment = useMemo(() => nextAppointment(appointments), [appointments]);
  const cv = useMemo(() => consentView(consents, consentStatus), [consents, consentStatus]);
  const recentFailure = useMemo(
    () => (messages || []).slice(-6).some(m => m.status === 'failed' || m.status === 'FAILED' || m.event_type === 'opt_out'),
    [messages]
  );

  const busy = !!escalation || cv.anyOff || documents.length > 0 || recentFailure;
  const railMode = override || (pin === 'auto' ? (busy ? 'open' : 'strip') : pin);
  const railClass = railMode === 'hidden' ? 'rail-hidden' : railMode === 'strip' ? 'rail-strip' : '';

  // ---- timeline: messages + events, in time order, folded per thread --------
  const subConversations = useMemo(() => conversation?.sub_conversations || [], [conversation?.sub_conversations]);
  const timeline = useMemo(() => buildTimeline(messages || [], subConversations, appointments), [messages, subConversations, appointments]);
  const segments = useMemo(
    () => buildThreadSegments(timeline, subConversations, conversation?.active_sub_conversation_id),
    [timeline, subConversations, conversation?.active_sub_conversation_id]
  );
  // The timeline earns its place as soon as anything is resolved, not only once a
  // second thread exists: resolving the only thread in a conversation should still
  // fold it to a card and show the "No open thread" end node. The header picker is
  // separate — it is only useful when there is more than one thread to jump between.
  const showTimeline = segments.some(s => s.closed);
  const multiThread = segments.length > 1;
  const closedCount = segments.filter(s => s.closed).length;
  const isOpenSeg = (seg) => !seg.closed || expandedThreads.has(seg.id);
  const toggleThread = (id) => setExpandedThreads(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const jumpToThread = (id) => {
    setExpandedThreads(prev => new Set(prev).add(id));
    setShowThreadMenu(false);
    window.requestAnimationFrame(() => segRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };
  const expandAllThreads = () => setExpandedThreads(new Set(segments.map(s => s.id)));
  const collapseAllThreads = () => setExpandedThreads(new Set());

  const handleResolve = () => {
    if (conversation.active_sub_conversation_id) {
      onResolve(conversation.active_sub_conversation_id, resolveNotes);
      setShowResolveModal(false);
      setResolveNotes('');
    }
  };
  const toggleReceipt = (id) => setOpenReceipts(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const consentPill = cv.anyOff
    ? { cls: 'consent off', text: `${cv.offCount} SMS channel${cv.offCount === 1 ? '' : 's'} off` }
    : (cv.known && (cv.legacyGranted || cv.rows.some(r => r.state === 'on')))
      ? { cls: 'consent', text: '✓ SMS consent' }
      : { cls: 'consent unknown', text: 'No SMS consent' };

  return (
    <div className={`nh-thread ${railClass}`}>
      {/* Header band */}
      <div className="nh-hdr">
        <div className="avatar" aria-hidden="true">{initialsOf(conversation?.first_name, conversation?.last_name)}</div>
        <div className="who">
          <h3 className="name">{conversation.first_name} {conversation.last_name}</h3>
          <div className="sub">
            <span className="mono">{formatPhone(conversation.patient_phone)}</span>
            <span className={`nh-pill ${consentPill.cls}`} title={cv.source || undefined}>
              {consentPill.text}
              {consentPill.cls === 'consent' && onRevokeConsent && cv.legacyGranted && (
                <button type="button" className="link" onClick={() => onRevokeConsent(conversation.patient_id)}>Revoke</button>
              )}
              {consentPill.cls !== 'consent' && onGrantConsent && !cv.legacyGranted && (
                <button type="button" className="link" onClick={() => onGrantConsent(conversation.patient_id)}>Grant</button>
              )}
            </span>
            {conversation.open_complaint && (
              <span className="nh-pill complaint" title="A complaint from this patient is waiting in the AI Queue">⚠ Complaint</span>
            )}
            {escalation && <span className="nh-pill escal">● Escalated · {escalation.reasonLabel}</span>}
            {!escalation && activeSub?.ai_takeover_status === 'human_takeover' && <span className="nh-pill human">You are responding</span>}
            {appointment && <span className="nh-pill appt">📅 {formatApptWhen(appointment)}{appointment.provider_name ? ` · ${appointment.provider_name}` : ''}</span>}
          </div>
        </div>
        <div className="actions">
          <AssignControl conversation={conversation} practiceId={practiceId} onChanged={onAiStatusChange} />
          <AiTakeoverControls practiceId={practiceId} subConversation={activeSub || conversation} onStatusChange={onAiStatusChange} />
          {multiThread && (
            <div className="nh-threads">
              <button type="button" className="btn ghost" aria-haspopup="menu" aria-expanded={showThreadMenu}
                onClick={() => setShowThreadMenu(v => !v)} title="Jump to an earlier thread">
                🗂 {segments.length} threads{closedCount ? ` · ${closedCount} resolved` : ''} ▾
              </button>
              {showThreadMenu && (
                <ThreadMenu segments={segments} isOpen={isOpenSeg} onJump={jumpToThread}
                  onExpandAll={expandAllThreads} onCollapseAll={collapseAllThreads} />
              )}
            </div>
          )}
          {conversation.active_sub_conversation_id && (
            <button type="button" className="btn resolve" onClick={() => setShowResolveModal(true)}>✓ Resolve thread</button>
          )}
          {railMode === 'hidden' && (
            <button type="button" className="btn ghost" onClick={() => setOverride('open')} title="Show patient context">‹ Context</button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="nh-loading"><div className="spin" />Loading messages…</div>
      ) : (
        <>
          <div className="nh-msgs" ref={msgsRef} onScroll={() => { stickRef.current = atBottom(); }}>
            {timeline.length === 0 ? (
              <div className="nh-empty"><b>No messages yet</b><span>Send a message to start the conversation</span></div>
            ) : (() => {
              const renderItems = (items) => items.map((item, i) => {
                const showDay = i === 0 || new Date(item.at).toDateString() !== new Date(items[i - 1].at).toDateString();
                return (
                  <React.Fragment key={item.key}>
                    {showDay && <div className="nh-day">{dayLabel(item.at)}</div>}
                    {item.kind === 'message'
                      ? <MessageRow message={item.message} open={openReceipts.has(item.message.id)} onToggle={() => toggleReceipt(item.message.id)} onOpenDocument={onOpenDocument} />
                      : <EventPill event={item} />}
                  </React.Fragment>
                );
              });
              if (!showTimeline) return <div className="nh-seg current open">{renderItems(segments[0]?.items || timeline)}</div>;
              const closed = segments.filter(s => s.closed);
              const current = segments.find(s => !s.closed) || null;
              return (
                <>
                  {/* Resolved threads: a dated timeline, oldest at the top, the spine running down into the open thread. */}
                  <div className="nh-tl" role="list" aria-label="Resolved threads">
                    {closed.map((seg, idx) => (
                      <TimelineThread key={seg.id} seg={seg} side={idx % 2 === 0 ? 'left' : 'right'} open={isOpenSeg(seg)}
                        onToggle={() => toggleThread(seg.id)} cardRef={el => { segRefs.current[seg.id] = el; }}>
                        {isOpenSeg(seg) && renderItems(seg.items)}
                      </TimelineThread>
                    ))}
                    {!current && (
                      <div className="nh-tl-item end" role="listitem">
                        <div className="node now" aria-hidden="true">●</div>
                        <div className="card note">
                          <span className="t">No open thread</span>
                          <span className="d">Your reply, or the patient's next text, starts a new one</span>
                        </div>
                      </div>
                    )}
                  </div>
                  {current && (
                    <div className="nh-seg current open" ref={el => { segRefs.current[current.id] = el; }}>
                      <ThreadFold seg={current} />
                      {renderItems(current.items)}
                    </div>
                  )}
                </>
              );
            })()}
            <div ref={endRef} />
          </div>

          <ContextRail
            mode={railMode === 'hidden' ? 'open' : railMode}
            pin={pin}
            onPin={setPin}
            onModeChange={(m) => setOverride(m)}
            conversation={conversation}
            practiceId={practiceId}
            patient={patient}
            summary={summary}
            summaryLoading={summaryLoading}
            onRefreshSummary={onRefreshSummary}
            appointment={appointment}
            consents={consents}
            legacyConsent={consentStatus}
            escalation={escalation}
            insights={insights}
            documents={documents}
            documentCategories={documentCategories}
            busyDocumentId={busyDocumentId}
            onGrantConsent={onGrantConsent}
            onRevokeConsent={onRevokeConsent}
            onAiStatusChange={onAiStatusChange}
            onBookingLink={onBookingLink}
            onRequestDocument={onRequestDocument}
            onSendDocuments={onSendDocuments}
            onOpenDocument={onOpenDocument}
            onRecategorize={onRecategorize}
            onPushDocument={onPushDocument}
          />

          <div className="nh-composer">{children}</div>
        </>
      )}

      {showResolveModal && (
        <div className="nh-modal-bg">
          <div className="nh-modal" role="dialog" aria-modal="true" aria-labelledby="nh-resolve-title">
            <h4 id="nh-resolve-title">Resolve this thread</h4>
            <p>The conversation stays in the inbox. A new thread starts for the patient's next message.</p>
            <textarea rows={3} placeholder="Resolution notes (optional)…" value={resolveNotes} onChange={e => setResolveNotes(e.target.value)} />
            <div className="row">
              <button type="button" className="btn" onClick={() => setShowResolveModal(false)}>Cancel</button>
              <button type="button" className="btn primary" onClick={handleResolve}>✓ Resolve thread</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function buildTimeline(messages, subConversations, appointments) {
  const items = messages
    .filter(m => m && m.created_at)
    .map(m => ({ kind: 'message', key: `m-${m.id}`, at: m.created_at, message: m }));
  if (!items.length) return items;
  const first = new Date(items[0].at).getTime();
  const inRange = (ts) => { const t = new Date(ts).getTime(); return !isNaN(t) && t >= first - 60000; };

  for (const sc of subConversations || []) {
    if (sc.escalated_at && inRange(sc.escalated_at)) {
      items.push({ kind: 'event', cls: 'esc', key: `esc-${sc.id}`, at: sc.escalated_at, subId: sc.id,
        text: `⚑ Escalated to the care team · ${humanize(escalationReason(sc.escalation_reason))}` });
    }
    if (sc.human_takeover_at && inRange(sc.human_takeover_at)) {
      items.push({ kind: 'event', cls: 'human', key: `hum-${sc.id}`, at: sc.human_takeover_at, subId: sc.id, text: '👤 Staff took over from the AI assistant' });
    }
    if (String(sc.status).toLowerCase() === 'closed' && sc.resolved_at && inRange(sc.resolved_at)) {
      const by = sc.resolved_by_name ? ` by ${sc.resolved_by_name}` : '';
      items.push({ kind: 'event', cls: 'resolved', key: `res-${sc.id}`, at: sc.resolved_at, subId: sc.id,
        text: `✓ Thread resolved${by} · ${shortDate(sc.resolved_at)}`, sub: sc.resolution_notes || null });
    }
  }
  for (const a of appointments || []) {
    if (a?.patient_confirmed_at && inRange(a.patient_confirmed_at)) {
      items.push({ kind: 'event', cls: 'ok', key: `conf-${a.id}`, at: a.patient_confirmed_at,
        text: `✓ Appointment confirmed by patient${a.appointment_date ? ` · ${formatApptWhen(a)}` : ''}` });
    }
  }
  // Stable sort: by time, events after the message that share a timestamp.
  return items
    .map((it, i) => ({ ...it, _i: i }))
    .sort((x, y) => (new Date(x.at) - new Date(y.at)) || (x.kind === y.kind ? x._i - y._i : x.kind === 'message' ? -1 : 1));
}

/**
 * One row per thread in the ledger. A resolved thread folds to this row and
 * opens on click; the current thread keeps a non-clickable label so the reader
 * can see where the earlier threads end and the live one begins.
 */
function ThreadFold({ seg }) {
  const count = `${seg.messageCount} message${seg.messageCount === 1 ? '' : 's'}`;
  return (
    <div className="nh-fold current" role="separator" aria-label="Current thread">
      <span className="chev">●</span>
      <span className="t">Current thread</span>
      <span className="d">{seg.firstAt ? `since ${shortDate(seg.firstAt)}` : 'no messages yet'}{seg.messageCount ? ` · ${count}` : ''}</span>
    </div>
  );
}

/**
 * One resolved thread on the timeline: a node on the spine, a card to one side
 * (title, dates, count, who resolved it, their note), and when opened the
 * thread's messages across the full width beneath it.
 */
function TimelineThread({ seg, side, open, onToggle, cardRef, children }) {
  const count = `${seg.messageCount} message${seg.messageCount === 1 ? '' : 's'}`;
  const resolved = seg.resolvedAt ? `Resolved ${shortDate(seg.resolvedAt)}${seg.resolvedBy ? ` by ${seg.resolvedBy}` : ''}` : 'Resolved';
  return (
    <div className={`nh-tl-item ${side}${open ? ' open' : ''}`} role="listitem">
      <div className="node" aria-hidden="true">✓</div>
      <button type="button" className="card" onClick={onToggle} aria-expanded={open} ref={cardRef}
        title={open ? 'Hide this thread' : 'Show this thread'}>
        <span className="t">{seg.title}{seg.range ? ` — ${seg.range}` : ''}</span>
        <span className="d">{seg.messageCount ? `${count} · ` : ''}{resolved}</span>
        {seg.notes && <span className="q">“{seg.notes}”</span>}
        <span className="hint">{open ? 'Hide messages ▴' : 'Show messages ▾'}</span>
      </button>
      {open && <div className="body">{children}</div>}
    </div>
  );
}

/** Newest first, so the live thread sits on top and history runs down the list. */
function ThreadMenu({ segments, isOpen, onJump, onExpandAll, onCollapseAll }) {
  const rows = segments.slice().reverse();
  return (
    <div className="menu" role="menu" aria-label="Threads in this conversation">
      {rows.map(seg => (
        <button key={seg.id} type="button" role="menuitem" className="item" onClick={() => onJump(seg.id)}>
          <span className="t">{seg.closed ? seg.title : 'Current thread'}</span>
          <span className={`s ${seg.closed ? 'closed' : 'live'}`}>{seg.closed ? 'Resolved' : 'Open'}</span>
          <span className="d">
            {seg.range || '—'} · {seg.messageCount} message{seg.messageCount === 1 ? '' : 's'}
            {seg.closed && !isOpen(seg) ? ' · folded' : ''}
          </span>
        </button>
      ))}
      <div className="foot">
        <button type="button" onClick={onExpandAll}>Expand all</button>
        <button type="button" onClick={onCollapseAll}>Collapse resolved</button>
      </div>
    </div>
  );
}

function MessageRow({ message, open, onToggle, onOpenDocument }) {
  const isInbound = message.direction === 'inbound';
  const isAi = message.sender_type === 'system';
  const isNotification = message.sender_type === 'notification' || message.is_notification;
  const time = timeOf(message.created_at);
  const status = String(message.status || '').toLowerCase();
  const failed = status === 'failed';

  // Ownership change. Internal only — nothing was texted to the patient.
  if (message.event_type === 'assignment') {
    return <div className="nh-event human"><span>👤 {message.content} · {time}</span></div>;
  }

  // Notices sent to the patient: shown as events with the text on demand.
  if (message.event_type === 'phi_notice' || message.event_type === 'consent_request') {
    const label = message.event_type === 'phi_notice' ? '🔒 Privacy notice sent' : '✋ Texting consent requested';
    return (
      <div className={`nh-event notice${failed ? ' failed' : ''}`}>
        <span>{label} · {time}{failed ? ' · not delivered' : ''}</span>
        <button type="button" onClick={onToggle}>{open ? 'Hide text' : 'Show text'}</button>
        {open && <small className="full">{message.content}</small>}
      </div>
    );
  }

  // Secure document link: uploads, sends and chart pushes are rows in the thread too.
  if (['document_received', 'document_sent', 'document_pushed'].includes(message.event_type)) {
    const meta = parseMeta(message.event_meta);
    const icon = message.event_type === 'document_received' ? '📎' : message.event_type === 'document_pushed' ? '🗂' : '📤';
    const who = message.event_type === 'document_received' ? 'Patient' : ([message.sender_first_name, message.sender_last_name].filter(Boolean).join(' ') || 'Staff');
    return (
      <div className="nh-event doc">
        <span>{icon} {who}: {message.content} · {time}</span>
        {meta.document_id && onOpenDocument && <button type="button" onClick={() => onOpenDocument({ id: meta.document_id })}>Open</button>}
      </div>
    );
  }

  // Migration 176: STOP / START are rows in the thread, shown as events.
  if (message.event_type === 'opt_out' || message.event_type === 'opt_in') {
    const meta = parseMeta(message.event_meta);
    const n = Number(meta.affected_patients || 0);
    const shared = n > 1 ? ` · ${n} patient records share this number` : '';
    const via = meta.source === 'PINPOINT_OPT_OUT' ? 'carrier opt-out' : `texted ${message.content || 'STOP'}`;
    return (
      <div className={`nh-event ${message.event_type === 'opt_out' ? 'stop' : 'ok'}`}>
        <span>
          {message.event_type === 'opt_out'
            ? `⛔ Patient ${via} · reminders, campaigns, surveys and payments paused${shared}`
            : `✓ Patient ${via} · all SMS channels restored${shared}`} · {time}
        </span>
      </div>
    );
  }

  if (isNotification) {
    return (
      <div className={`nh-row sys${failed ? ' fail' : ''}`}>
        <div className="rail" />
        <div className="nh-rem">
          <span className="k">🔔 {message.action_type ? humanize(message.action_type).replace(/ appt/i, ' appointment') : 'Reminder'}</span>
          <span className="sum" title={message.content}>{summarize(message.content)}</span>
          <span className="t">{time} · <Status status={status} /></span>
          <button type="button" className="more" onClick={onToggle}>{open ? 'Hide text' : 'Show text'}</button>
          {open && <div className="full">{message.content}</div>}
        </div>
      </div>
    );
  }

  const cls = isInbound ? 'patient' : isAi ? 'ai' : 'staff';
  const staffName = [message.sender_first_name, message.sender_last_name].filter(Boolean).join(' ');
  return (
    <div className={`nh-row ${cls}${isInbound ? '' : ' right'}${failed ? ' failed' : ''}`}>
      {isInbound && <div className="rail" />}
      <div>
        <div className="nh-bub">{message.content}</div>
        <div className="nh-meta">
          <span className="tag">{isInbound ? 'Patient' : isAi ? '✦ AI assistant' : staffName ? `${staffName} · staff` : 'Staff'}</span>
          <span>{time}</span>
          {isInbound && message.ai_intent && (
            <span className="intent">· intent: <b>{String(message.ai_intent).toLowerCase().replace(/_/g, ' ')}</b>{message.ai_confidence != null ? ` · ${Number(message.ai_confidence).toFixed(2)}` : ''}</span>
          )}
          {!isInbound && <Status status={status} error={message.error_message} />}
        </div>
      </div>
      {!isInbound && <div className="rail" />}
    </div>
  );
}

function Status({ status, error }) {
  switch (status) {
    case 'delivered': return <span className="nh-st ok">✓✓ delivered</span>;
    case 'sent': return <span className="nh-st ok" title="Accepted by the carrier network">✓ sent</span>;
    case 'failed': return <span className="nh-st fail" title={error || undefined}>✕ {error ? shortError(error) : 'failed'}</span>;
    case 'received': return null;
    case 'pending': return <span className="nh-st wait">… sending</span>;
    default: return status ? <span className="nh-st wait">{status}</span> : null;
  }
}

function parseMeta(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw) || {}; } catch { return {}; }
}

function shortError(e) {
  const s = String(e);
  if (/opt.?out|blocked|STOP/i.test(s)) return 'blocked · patient opted out';
  if (/unreachable|undeliverable|invalid/i.test(s)) return 'carrier unreachable';
  return s.length > 40 ? s.slice(0, 40) + '…' : s;
}

function summarize(content) {
  if (!content) return '';
  const oneLine = String(content).replace(/\s+/g, ' ').trim();
  const firstSentence = oneLine.split(/(?<=[.!?])\s/)[0] || oneLine;
  const s = firstSentence.length >= 40 ? firstSentence : oneLine;
  return s.length > 120 ? s.slice(0, 117) + '…' : s;
}

function EventPill({ event }) {
  return (
    <div className={`nh-event ${event.cls}`}>
      <span>{event.text} · {timeOf(event.at)}</span>
      {event.sub && <small>{event.sub}</small>}
    </div>
  );
}

export default ConversationThread;
